import { array, option, record } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { groupBy } from 'lodash/fp';
import { createSelector } from 'reselect';

import {
    Amount,
    convertGrossAmountToSimpleAmount,
    convertNetAmountToSimpleAmount,
    createReduceAndSumAmounts,
    createZeroAmount,
    createZeroSimpleAmount,
    divideAmount,
    isZeroAmount,
    subtractUntaxedAmounts,
    sumAmounts,
    sumSimpleAmounts,
    UntaxedAmount,
} from '@mews-distributor/common';
import { ProductChargingMode, TimeUnit } from '@mews/enums';
import { getBooleanProp, getNumberProp, getProp, getStringProp } from '@mews/fp';

import { getAgeCategoryLocalizedName } from '../../selectors/views/ageCategories';
import { getSelectedEnterpriseAmountConvertor } from '../../selectors/views/selectedEnterprise/selectedEnterprise';
import { ApplicationState } from '../../types';
import { WithReservationId } from '../../types/selectors/selectorPropTypes';
import { getCurrencyCode } from '../app/selectors';
import { hasEnterpriseNetPricing } from '../entities/enterprises/selectors/selectors';
import { getReservationPrices, getReservationPricesData } from '../entities/reservationPrices/selectors';
import {
    getCurrentReservationGroupId,
    getMerchantCurrencyCodeOrEnterpriseDefaultCurrencyCode,
    getReservationProductIds,
    getReservationTotalPersonCount,
    getTotalReservationOccupancyData,
} from '../entities/reservations/selectors';
import { getEnterpriseServiceId, getServiceTimeUnit, isServiceDayUse } from '../entities/services/selectors';
import { getSelectedHotelId } from '../resourceOrder/selectors';
import { ProductOptions, ProductOrder } from './types';

export const getProductOrders = createSelector(getReservationPricesData, (reservationPrices) =>
    reservationPrices
        ? pipe(
              reservationPrices,
              record.toArray,
              array.flatMap(([_reservationGroupId, productPrice]) =>
                  productPrice.flatMap(({ productOrderPrices, identifier }) =>
                      productOrderPrices.map((productOrder) => ({ ...productOrder, reservationId: identifier }))
                  )
              )
          )
        : []
);
export const getReservationProductOrders = createSelector(
    getProductOrders,
    getStringProp('reservationId'),
    (productOrders, reservationId) =>
        pipe(
            productOrders,
            array.filter(({ reservationId: productOrderReservationId }) => reservationId === productOrderReservationId)
        )
);

const getProductUnitPrice = createSelector(
    getProp<UntaxedAmount>()('averageAmount'),
    getProp<ProductChargingMode>()('chargingMode'),
    getNumberProp('personCount'),
    (averageAmount, chargingMode, personCount) => {
        switch (chargingMode) {
            case ProductChargingMode.PerPerson:
            case ProductChargingMode.PerPersonPerTimeUnit:
                return divideAmount(averageAmount, personCount);
            default:
                return averageAmount;
        }
    }
);

interface IsProductIncludedInRateParams {
    productIds: string[];
    productId: string;
    productOptions: ProductOptions;
}

const isProductIncludedInRate = ({ productIds, productId, productOptions }: IsProductIncludedInRateParams) =>
    !(productIds.includes(productId) || productOptions.excludePriceFromOffer);

/**
 * Removes product orders with zero total amount when all product orders have non-zero total amount.
 * @param productOrders - Product orders to filter.
 */
const removeZeroProductOrders = (productOrders: ProductOrder[]) => {
    const hasEveryProductOrderZeroAmount = pipe(
        productOrders,
        array.every(({ totalAmount }) => isZeroAmount(totalAmount))
    );

    // when all product orders have zero total amount, we want to return all product orders ( free product )
    if (hasEveryProductOrderZeroAmount) {
        return productOrders;
    }

    return pipe(
        productOrders,
        array.filter(({ totalAmount }) => !isZeroAmount(totalAmount))
    );
};

interface GetEnhancedProductOrderPricesParams extends WithReservationId {
    shouldSelectedCurrencyBeUsed?: boolean;
    useClassificationForPriceVisibility?: boolean;
}

export const getEnhancedProductOrderPrices = (
    state: ApplicationState,
    {
        reservationId,
        shouldSelectedCurrencyBeUsed = false,
        useClassificationForPriceVisibility = false,
    }: GetEnhancedProductOrderPricesParams
) => {
    const reservationTotalPersonCount = getReservationTotalPersonCount(state, { reservationId });
    const personCount = Math.max(1, reservationTotalPersonCount);
    const enterpriseId = getSelectedHotelId(state);
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    const serviceId = getEnterpriseServiceId(state, { enterpriseId }) || '';
    const personCountByAge = getTotalReservationOccupancyData(state, { reservationId, serviceId });
    const currencyCode = shouldSelectedCurrencyBeUsed
        ? getCurrencyCode(state)
        : getMerchantCurrencyCodeOrEnterpriseDefaultCurrencyCode(state, { enterpriseId });
    const productIds = getReservationProductIds(state, { reservationId });
    const hasNet = hasEnterpriseNetPricing(state, { enterpriseId });
    const reservationProductOrders = getReservationProductOrders(state, { reservationId });
    const convertAmount = hasNet ? convertNetAmountToSimpleAmount : convertGrossAmountToSimpleAmount;
    const isDayService = isServiceDayUse(state, { enterpriseId });
    const timeUnit = getServiceTimeUnit(state, { enterpriseId });

    return pipe(
        reservationProductOrders,
        groupBy<ProductOrder>(({ productId }) => productId),
        record.toArray,
        array.map(([productId, baseProductOrders]) => {
            const productOrders = removeZeroProductOrders(baseProductOrders);
            const totalPrice = pipe(
                productOrders,
                array.reduce<ProductOrder, Amount>(createZeroAmount(currencyCode), (prev, { totalAmount }) =>
                    sumAmounts(prev, totalAmount)
                ),
                convertAmount
            );
            const firstProductOrder = productOrders[0];
            if (firstProductOrder == null) {
                return option.none;
            }
            const { chargingMode, productName, productOptions, classifications } = firstProductOrder;
            const quantity = array.size(productOrders);

            const productOrderAgeCategories = pipe(
                productOrders,
                array.filter(({ ageCategoryId }) => ageCategoryId != null),
                groupBy<ProductOrder>(({ ageCategoryId }) => ageCategoryId),
                record.toArray,
                array.map(([ageCategoryId]) => ageCategoryId)
            );

            const quantityPerAgeCategory = productOrderAgeCategories.map((ageCategoryId) => {
                const filtered = productOrders.filter((order) => order.ageCategoryId === ageCategoryId);
                const quantityOfAge = array.size(filtered);
                const ageCategoryPersonCount =
                    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
                    personCountByAge.find((item) => item.ageCategoryId === ageCategoryId)?.personCount || 1;
                const averageAmount = divideAmount(
                    filtered.reduce(
                        (prev, { totalAmount }) => sumAmounts(prev, totalAmount),
                        createZeroAmount(currencyCode)
                    ),
                    quantityOfAge
                );
                const unitPrice = convertAmount(
                    getProductUnitPrice(state, {
                        averageAmount,
                        personCount: ageCategoryPersonCount,
                        chargingMode,
                    })
                );

                const ageCategoryLocalizedName = getAgeCategoryLocalizedName(state, { ageCategoryId });

                return {
                    ageCategoryId,
                    ageCategoryLocalizedName,
                    isDayService: isDayService && timeUnit !== TimeUnit.Hour,
                    personCount: ageCategoryPersonCount,
                    quantity: quantityOfAge,
                    unitPrice,
                    timeUnit,
                };
            });

            const averageAmount = divideAmount(
                pipe(
                    productOrders,
                    array.reduce<ProductOrder, Amount>(createZeroAmount(currencyCode), (prev, { totalAmount }) =>
                        sumAmounts(prev, totalAmount)
                    )
                ),
                quantity
            );

            const unitPrice = convertAmount(
                getProductUnitPrice(state, {
                    averageAmount,
                    personCount,
                    chargingMode,
                })
            );

            const isIncludedInRate = isProductIncludedInRate({
                productIds,
                productId,
                productOptions,
            });

            const isDisplayingPrice = useClassificationForPriceVisibility
                ? classifications.fee === true || classifications.cityTax === true
                : !isIncludedInRate;

            return option.some({
                id: productId,
                chargingMode,
                personCount,
                quantity,
                quantityPerAgeCategory,
                unitPrice,
                name: productName,
                totalPrice,
                isIncludedInRate,
                isDisplayingPrice,
            });
        }),
        array.compact
    );
};

export const getProductOrdersTotalPrice = createSelector(
    getEnhancedProductOrderPrices,
    getMerchantCurrencyCodeOrEnterpriseDefaultCurrencyCode,
    (productOrders, currencyCode) =>
        pipe(
            productOrders,
            array.filter((productOrder) => !productOrder.isIncludedInRate),
            array.reduce(createZeroSimpleAmount(currencyCode), (acc, { totalPrice }) =>
                sumSimpleAmounts(acc, totalPrice)
            )
        )
);

export const getProductOrdersTotalPriceInSelectedCurrency = createSelector(
    (state: ApplicationState, reservationId: string) =>
        getEnhancedProductOrderPrices(state, { shouldSelectedCurrencyBeUsed: true, reservationId }),
    getCurrencyCode,
    (productOrders, currencyCode) =>
        pipe(
            productOrders,
            array.filter((productOrder) => !productOrder.isIncludedInRate),
            array.reduce(createZeroSimpleAmount(currencyCode ?? ''), (acc, { totalPrice }) =>
                sumSimpleAmounts(acc, totalPrice)
            )
        )
);

export const getCurrentReservationTotalPriceWithoutUserProducts = (
    state: ApplicationState,
    { reservationId, shouldSelectedCurrencyBeUsed }: WithReservationId & { shouldSelectedCurrencyBeUsed: boolean }
) => {
    const reservationGroupId = getCurrentReservationGroupId(state);

    return getReservationTotalPriceWithoutUserProducts(state, {
        reservationGroupId,
        reservationId,
        shouldSelectedCurrencyBeUsed,
    });
};

const getReservationTotalPriceWithoutUserProducts = createSelector(
    getSelectedEnterpriseAmountConvertor,
    getReservationProductOrders,
    getReservationProductIds,
    getReservationPrices,
    getMerchantCurrencyCodeOrEnterpriseDefaultCurrencyCode,
    getCurrencyCode,
    getStringProp('reservationId'),
    getBooleanProp('shouldSelectedCurrencyBeUsed'),
    (
        convertAmount,
        productOrders,
        userSelectedProductIds,
        reservationPrices,
        defaultCurrencyCode,
        selectedCurrencyCode,
        reservationId,
        shouldSelectedCurrencyBeUsed
    ) => {
        const currencyCode = shouldSelectedCurrencyBeUsed ? selectedCurrencyCode : defaultCurrencyCode;
        const reservationTotalAmount = pipe(
            reservationPrices,
            array.filter((reservation) => reservation.identifier === reservationId),
            array.map(({ totalAmount }) => totalAmount),
            createReduceAndSumAmounts(currencyCode)
        );

        const userSelectedProductsTotalAmount = pipe(
            productOrders,
            array.filter(
                ({ productId, productOptions }) =>
                    !isProductIncludedInRate({
                        productIds: userSelectedProductIds,
                        productId,
                        productOptions,
                    })
            ),
            array.map(({ totalAmount }) => totalAmount),
            createReduceAndSumAmounts(currencyCode)
        );

        return pipe(subtractUntaxedAmounts(reservationTotalAmount, userSelectedProductsTotalAmount), convertAmount);
    }
);

export const getCurrentReservationUnitPriceWithoutUserProducts = (
    state: ApplicationState,
    { reservationId, shouldSelectedCurrencyBeUsed }: WithReservationId & { shouldSelectedCurrencyBeUsed: boolean }
) => {
    const reservationGroupId = getCurrentReservationGroupId(state);
    return getReservationUnitPriceWithoutUserProducts(state, {
        reservationGroupId,
        reservationId,
        shouldSelectedCurrencyBeUsed,
    });
};

const getReservationUnitPriceWithoutUserProducts = createSelector(
    getSelectedEnterpriseAmountConvertor,
    getReservationProductIds,
    getMerchantCurrencyCodeOrEnterpriseDefaultCurrencyCode,
    getReservationPrices,
    getCurrencyCode,
    getStringProp('reservationId'),
    getBooleanProp('shouldSelectedCurrencyBeUsed'),
    (
        convertAmount,
        userSelectedProductIds,
        defaultCurrencyCode,
        reservationPrices,
        selectedCurrencyCode,
        reservationId,
        shouldSelectedCurrencyBeUsed
    ) => {
        const currencyCode = shouldSelectedCurrencyBeUsed ? selectedCurrencyCode : defaultCurrencyCode;
        const reservationPrice = pipe(
            reservationPrices,
            array.findFirst((reservation) => reservation.identifier === reservationId)
        );

        const reservationUnitTotalAmount = pipe(
            reservationPrice,
            option.map(({ totalAmount }) => totalAmount)
        );

        const userSelectedProductsAmountTotal = pipe(
            reservationPrice,
            option.map(({ productOrderPrices }) =>
                pipe(
                    productOrderPrices,
                    array.filter(
                        ({ productId, productOptions }) =>
                            !isProductIncludedInRate({
                                productIds: userSelectedProductIds,
                                productId,
                                productOptions,
                            })
                    ),
                    array.map(({ totalAmount }) => totalAmount),
                    createReduceAndSumAmounts(currencyCode)
                )
            )
        );

        if (option.isSome(reservationUnitTotalAmount) && option.isSome(userSelectedProductsAmountTotal)) {
            return pipe(
                subtractUntaxedAmounts(reservationUnitTotalAmount.value, userSelectedProductsAmountTotal.value),
                convertAmount
            );
        }

        return createZeroSimpleAmount(currencyCode);
    }
);
